import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _IconArrowOpenDownLin, _IconArrowOpenUpLine;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@instructure/ui-buttons';
import { IconArrowOpenDownLine, IconArrowOpenUpLine } from '@instructure/ui-icons';
import { Menu } from '@instructure/ui-menu';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { View } from '@instructure/ui-view';
import { TruncateText } from '@instructure/ui-truncate-text';
import { translate } from '../../../constants/translated/translations';
export const OFF_VALUE = 'off';
function formatLabel(device) {
  return device.label && device.label.length > 0 ? device.label : device.deviceId;
}
function mapInteraction(isDisabled) {
  return isDisabled ? 'disabled' : 'enabled';
}
function DeviceSelection({
  selectedDeviceId,
  devices,
  onDeviceSelected,
  onSubsectionSelected,
  label,
  icon,
  offLabel,
  primaryLabel,
  isDisabled,
  'data-testid': identifier,
  color,
  mobile,
  subsection,
  selectedSubsectionValues
}) {
  var _Menu$Item;
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showMenu = _useState2[0],
    setShowMenu = _useState2[1];
  const handleDeviceSelected = useCallback((_, [newSelected]) => {
    if (!newSelected) return;
    onDeviceSelected(newSelected);
  }, [onDeviceSelected]);
  const handleSubsectionSelected = useCallback((_, __, ___, item) => {
    onSubsectionSelected(item.props.value);
  }, [onSubsectionSelected]);
  function getCurrentDeviceText() {
    const selectedDevice = devices.find(el => el.deviceId === selectedDeviceId);
    return selectedDevice ? formatLabel(selectedDevice) : offLabel;
  }
  const menuItems = useMemo(() => {
    const items = devices.map(d => {
      return /*#__PURE__*/React.createElement(Menu.Item, {
        key: `${d.deviceId}`,
        value: `${d.deviceId}`
      }, /*#__PURE__*/React.createElement(View, {
        as: "div",
        width: "11.875rem"
      }, /*#__PURE__*/React.createElement(TruncateText, null, formatLabel(d))));
    });
    if (offLabel) {
      items.push(_Menu$Item || (_Menu$Item = /*#__PURE__*/React.createElement(Menu.Item, {
        key: OFF_VALUE,
        value: OFF_VALUE
      }, /*#__PURE__*/React.createElement(View, {
        as: "div",
        width: "11.875rem"
      }, /*#__PURE__*/React.createElement(TruncateText, null, offLabel)))));
    }
    return items;
  }, [devices, offLabel]);
  return /*#__PURE__*/React.createElement(Menu, {
    placement: "bottom",
    show: showMenu,
    onToggle: showMenu => {
      setShowMenu(showMenu);
    },
    onKeyUp: e => {
      if (e.key === 'Escape') {
        e.preventDefault(); // don't let the handler on the <Modal> cause the whole modal to close
        setShowMenu(false);
      }
    },
    trigger:
    /*#__PURE__*/
    // className is needed for impact app
    React.createElement(View, {
      as: "span",
      className: identifier
    }, /*#__PURE__*/React.createElement(Button, {
      "aria-disabled": isDisabled,
      "aria-labelledby": primaryLabel,
      "aria-expanded": showMenu,
      display: "block",
      color: color,
      withBackground: color === 'primary-inverse',
      size: "medium",
      "data-testid": identifier,
      themeOverride: {
        iconSizeMedium: '1.125rem'
      },
      interaction: mapInteraction(isDisabled),
      renderIcon: icon,
      "aria-label": `${label}, ${translate('SELECTED_VALUE')} ${getCurrentDeviceText()}`
    }, mobile ? /*#__PURE__*/React.createElement(TruncateText, null, getCurrentDeviceText()) : showMenu ? _IconArrowOpenDownLin || (_IconArrowOpenDownLin = /*#__PURE__*/React.createElement(IconArrowOpenDownLine, null)) : _IconArrowOpenUpLine || (_IconArrowOpenUpLine = /*#__PURE__*/React.createElement(IconArrowOpenUpLine, null))))
  }, /*#__PURE__*/React.createElement(Menu.Group, {
    label: label,
    selected: selectedDeviceId ? [selectedDeviceId] : [OFF_VALUE],
    onSelect: handleDeviceSelected
  }, menuItems), subsection && subsection.items.length > 0 && /*#__PURE__*/React.createElement(Menu.Group, {
    label: /*#__PURE__*/React.createElement(ScreenReaderContent, null, subsection.label),
    selected: selectedSubsectionValues,
    onSelect: handleSubsectionSelected
  }, /*#__PURE__*/React.createElement(Menu.Separator, {
    themeOverride: {
      margin: '0.5rem 0.75rem'
    }
  }), subsection.items.map(item => /*#__PURE__*/React.createElement(Menu.Item, {
    key: item.value,
    value: item.value,
    disabled: item.disabled
  }, /*#__PURE__*/React.createElement(View, {
    as: "div",
    width: "11.875rem"
  }, item.label)))));
}
DeviceSelection.propTypes = {
  selectedDeviceId: PropTypes.string,
  devices: PropTypes.arrayOf(PropTypes.shape({
    deviceId: PropTypes.string,
    label: PropTypes.string
  })).isRequired,
  onDeviceSelected: PropTypes.func.isRequired,
  onSubsectionSelected: PropTypes.func,
  label: PropTypes.string.isRequired,
  primaryLabel: PropTypes.string,
  icon: PropTypes.element.isRequired,
  offLabel: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  'data-testid': PropTypes.string,
  color: PropTypes.oneOf(['danger', 'primary-inverse']),
  mobile: PropTypes.bool,
  subsection: PropTypes.shape({
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      disabled: PropTypes.bool
    }))
  }),
  selectedSubsectionValues: PropTypes.arrayOf(PropTypes.string)
};
DeviceSelection.defaultProps = {
  'data-testid': '',
  color: 'primary-inverse',
  selectedDeviceId: '',
  onSubsectionSelected: () => {},
  mobile: false,
  subsection: null,
  primaryLabel: '',
  selectedSubsectionValues: []
};
export default DeviceSelection;